import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Janela, JanelaDeAcao } from '@/componentes/JanelaDeAcao';
import { EtiquetaDoPreenchido, LinhaDoPreenchido } from '@/componentes/LinhaDoPreenchido';
import { MaisAcoes, type AcaoDoMenu } from '@/componentes/MaisAcoes';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { HistoriaNaMesa } from '@/historias/mesa';
import { usePaginacao } from '@/modulos/operacao/paginacao';
import { formatarInteiro, interpretarNumero } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';
import { useSessao } from '@/modulos/sessao/consultas';
import { useCatalogo } from '@/modulos/equipamentos/consultas-frota';

import { api, type Checklist, type ItemDoChecklist, type RotinaDoChecklist } from './api';
import { criticidadeEscrita, porqueNaoAcrescenta, proximaOrdem, rotinaEscrita, situacaoDoChecklistEscrita } from './inspecao';
import { opcaoDaConfiguracao, opcaoDoModelo } from '@/modulos/equipamentos/opcoesComVisual';

/**
 * MÓDULO 10 — a montagem do checklist. Segunda tela do módulo.
 *
 * ## Por que ela é uma página, e não uma janela
 *
 * O manual (R01, p.28) é direto: cadastro completo, mais de dez campos ou
 * várias seções é **página inteira**, não modal. Montar um checklist é uma
 * lista de itens que cresce — dez, vinte, trinta linhas —, e cada item tem
 * ordem, grupo, descrição, tipo, unidade e criticidade. Numa janela de 640 px
 * isso rola por baixo do próprio rodapé.
 *
 * ## A ordem dos três passos não é escolha de interface
 *
 * Montar em rascunho, acrescentar os itens, e só então publicar com a nota da
 * revisão técnica. O banco impõe essa ordem — item em checklist publicado é
 * recusado, e publicar sem item também —, e a tela a desenha na mesma sequência
 * porque desenhar outra faria a pessoa descobrir a regra pelo erro.
 *
 * ## Publicar é o fim de um caminho, não um botão a mais
 *
 * Ele arquiva o publicado anterior do mesmo alvo e rotina, e depois disso nada
 * muda: a inspeção de ontem aponta para a lista de ontem. A tela diz as duas
 * coisas antes de pedir a confirmação — o manual pede que a mensagem diga o que
 * vai acontecer, e aqui o que acontece é irreversível.
 */

const ROTINAS: readonly RotinaDoChecklist[] = ['diaria', 'semanal', 'entrega', 'devolucao', 'pos_manutencao'];

export function PaginaChecklists() {
  const cliente = useQueryClient();
  const sessao = useSessao();
  const permissoes = useMemo(() => sessao.data?.permissoes ?? [], [sessao.data]);
  const podeGravar = permissoes.includes('checklist.gravar');
  const podePublicar = permissoes.includes('checklist.publicar');

  const [criando, setCriando] = useState(false);
  const [nome, setNome] = useState('');
  const [rotina, setRotina] = useState<string>('diaria');
  const [modeloId, setModeloId] = useState('');
  const [configuracaoId, setConfiguracaoId] = useState('');
  /* Modelo e configuracao saem do catalogo: a janela pedia o identificador
     interno, que ninguem sabe de cabeca (achado no QAS em 20/10/2026). */
  const catalogo = useCatalogo();
  const opcoesDeModelo = useMemo(
    () =>
      (catalogo.data?.modelos ?? [])
        .filter((m) => m.ativo)
        .map((m) => opcaoDoModelo(m))
        .sort((a, b) => a.rotulo.localeCompare(b.rotulo, 'pt-BR')),
    [catalogo.data],
  );
  const opcoesDeConfiguracao = useMemo(
    () =>
      (catalogo.data?.configuracoes ?? [])
        .filter((c) => c.ativo)
        .map((c) => opcaoDaConfiguracao(c, [c.fabricante, c.modelo, c.nome].filter(Boolean).join(' · ')))
        .sort((a, b) => a.rotulo.localeCompare(b.rotulo, 'pt-BR')),
    [catalogo.data],
  );

  /* O checklist aberto para montagem. Null quer dizer "nenhum escolhido", e a
     tela mostra a lista; escolhido, ela mostra os itens dele. */
  const [aberto, setAberto] = useState<Checklist | null>(null);

  const [itemOrdem, setItemOrdem] = useState('');
  const [itemGrupo, setItemGrupo] = useState('');
  const [itemDescricao, setItemDescricao] = useState('');
  const [itemTipo, setItemTipo] = useState('conformidade');
  const [itemUnidade, setItemUnidade] = useState('');
  const [itemCriticidade, setItemCriticidade] = useState('atencao');
  const [acrescentando, setAcrescentando] = useState(false);
  const [ajustandoPosicao, setAjustandoPosicao] = useState(false);

  const [publicando, setPublicando] = useState<Checklist | null>(null);
  const [notaDeRevisao, setNotaDeRevisao] = useState('');

  const checklists = useQuery({ queryKey: ['inspecao', 'checklists'], queryFn: api.checklists });
  const itens = useQuery({
    queryKey: ['inspecao', 'itens', aberto?.id ?? null],
    queryFn: () => api.itensDoChecklist((aberto as Checklist).id),
    enabled: aberto !== null,
  });

  const recarregar = () => cliente.invalidateQueries({ queryKey: ['inspecao'] });
  /* A ordem chega SUGERIDA: a maior da lista, mais um (achado dos robôs,
     20/10/2026 — a janela abria com a ordem vazia e o botão desligado sem
     dizer por quê). */
  const abrirAcrescentar = () => {
    const lista = itens.data?.itens ?? [];
    setItemOrdem(String(proximaOrdem(lista)));
    /* O GRUPO do último item da lista (31/10/2026): os itens de um grupo se
       acrescentam em sequência, e o campo fica no ajuste, em etiqueta. */
    if (itemGrupo.trim() === '') {
      const ultimo = [...lista].sort((a, b) => b.ordem - a.ordem)[0];
      if (ultimo?.grupo) setItemGrupo(ultimo.grupo);
    }
    setAcrescentando(true);
  };
  const motivoDeNaoAcrescentar = porqueNaoAcrescenta({ ordem: itemOrdem, descricao: itemDescricao, tipo: itemTipo, unidade: itemUnidade });

  const criar = useMutation({
    mutationFn: () =>
      api.criarChecklist({
        nome,
        rotina: rotina as RotinaDoChecklist,
        modeloId: modeloId.trim() === '' ? null : modeloId,
        configuracaoId: configuracaoId.trim() === '' ? null : configuracaoId,
      }),
    meta: {
      retorno: retornoDe(() => ({
        titulo: 'Checklist criado',
        alvo: { nome },
        fatos: [rotina ? rotinaEscrita(rotina as RotinaDoChecklist) : null],
        efeito: 'Fica em rascunho: acrescente os itens e publique',
      })),
    },
    onSuccess: async () => {
      setCriando(false);
      setNome('');
      setModeloId('');
      setConfiguracaoId('');
      await recarregar();
    },
  });

  const acrescentarItem = useMutation({
    mutationFn: () => {
      const corpo: Parameters<typeof api.acrescentarItem>[1] = {
        ordem: interpretarNumero(itemOrdem) ?? 1,
        descricao: itemDescricao,
        tipo: itemTipo as ItemDoChecklist['tipo'],
        criticidade: itemCriticidade as ItemDoChecklist['criticidade'],
        ...(itemGrupo.trim() === '' ? {} : { grupo: itemGrupo }),
        ...(itemTipo === 'medida' ? { unidade: itemUnidade } : {}),
      };
      return api.acrescentarItem((aberto as Checklist).id, corpo);
    },
    meta: {
      retorno: retornoDe(() => ({
        titulo: 'Item acrescentado ao checklist',
        ...(aberto ? { alvo: { nome: aberto.nome } } : {}),
        fatos: [`${itemOrdem}. ${itemDescricao}`, itemCriticidade ? criticidadeEscrita(itemCriticidade as ItemDoChecklist['criticidade']) : null],
      })),
    },
    onSuccess: async () => {
      /* A JANELA FICA ABERTA (achado dos robôs, 20/10/2026): o texto dela
         prometia isso e esta mutação a fechava a cada
         item. Só a descrição e a unidade se limpam. */
      setItemDescricao('');
      setItemUnidade('');
      /* A ordem avança sozinha: montar trinta itens digitando o número de cada
         um é o tipo de atrito que faz a lista ficar pela metade. */
      setItemOrdem(String(proximaOrdem(itens.data?.itens ?? [], interpretarNumero(itemOrdem))));
      await recarregar();
      await cliente.invalidateQueries({ queryKey: ['inspecao', 'itens'] });
    },
  });

  const publicar = useMutation({
    mutationFn: () => api.publicarChecklist((publicando as Checklist).id, notaDeRevisao),
    meta: {
      retorno: retornoDe<{ arquivados: number }>((r) => ({
        titulo: 'Checklist publicado',
        ...(publicando ? { alvo: { nome: publicando.nome } } : {}),
        fatos: [notaDeRevisao.trim() ? `nota: ${notaDeRevisao.trim()}` : null],
        efeito: r.arquivados > 0 ? 'A versão anterior foi arquivada; as inspeções novas usam esta' : 'As inspeções novas já podem usar este checklist',
      })),
    },
    onSuccess: async () => {
      setPublicando(null);
      setNotaDeRevisao('');
      await recarregar();
    },
  });

  const lista = useMemo(() => checklists.data?.checklists ?? [], [checklists.data]);
  const paginaDosChecklists = usePaginacao(lista);
  const paginaDosItens = usePaginacao(itens.data?.itens ?? []);

  /* ----------------------------------------------- a lista de checklists */

  const COLUNAS: readonly Coluna<Checklist>[] = [
    {
      chave: 'nome',
      rotulo: 'Checklist',
      celula: (c) => (
        <span className="flex min-w-0 flex-col">
          <span className="txt-corpo text-tinta">{c.nome}</span>
          <TextoDaCelula
            texto={`${rotinaEscrita(c.rotina)} · ${c.configuracao ?? c.modelo ?? 'sem alvo'}`}
            classe="txt-apoio text-tinta-apagada"
            linhas={1}
          />
        </span>
      ),
    },
    {
      chave: 'situacao',
      rotulo: 'Situação',
      celula: (c) => <span className="txt-corpo text-tinta">{situacaoDoChecklistEscrita(c.situacao)}</span>,
    },
    {
      chave: 'itens',
      rotulo: 'Itens',
      alinhamento: 'numero',
      celula: (c) => <span className="numero text-tinta">{formatarInteiro(c.itens)}</span>,
    },
    {
      chave: 'criticos',
      rotulo: 'Críticos',
      alinhamento: 'numero',
      prioridade: 'secundaria',
      celula: (c) => <span className="numero text-tinta">{formatarInteiro(c.criticos)}</span>,
    },
  ];

  const acoesDoChecklist = (c: Checklist): readonly AcaoDoMenu[] => {
    /* Só as ações da licença de quem está logado (prova do marco, 07/10/2026):
       quem não monta só vê os itens; publicar só aparece para quem publica. */
    const publicarMotivo =
      c.situacao !== 'rascunho'
        ? 'Este checklist já foi publicado.'
        : c.itens === 0
          ? 'Acrescente ao menos um item antes de publicar: lista vazia não verifica nada.'
          : undefined;
    return [
      { rotulo: podeGravar && c.situacao === 'rascunho' ? 'Ver e montar os itens' : 'Ver os itens', aoEscolher: () => setAberto(c) },
      ...(podePublicar
        ? [
            {
              rotulo: 'Publicar com a nota de revisão',
              aoEscolher: () => setPublicando(c),
              ...(publicarMotivo === undefined ? {} : { motivoIndisponivel: publicarMotivo }),
            },
          ]
        : []),
    ];
  };

  const alvoInvalido = (modeloId.trim() === '') === (configuracaoId.trim() === '');

  return (
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina flex flex-col gap-6">
        <CabecalhoPagina
          sobretitulo="Checklists e ocorrências"
          titulo="Montar os checklists"
          subtitulo="A lista de verificação de cada modelo ou configuração, publicada com a nota da revisão técnica"
          acoes={
            podeGravar ? (
            <JanelaDeAcao
              rotulo="Novo checklist"
              titulo="Criar um checklist em rascunho"
              nivel="primario"
              aberta={criando}
              aoAbrir={() => setCriando(true)}
              aoFechar={() => setCriando(false)}
              acoes={
                <>
                  <Botao nivel="terciario" onClick={() => setCriando(false)}>
                    Cancelar
                  </Botao>
                  <Botao
                    nivel="primario"
                    disabled={nome.trim() === '' || alvoInvalido || criar.isPending}
                    onClick={() => criar.mutate()}
                  >
                    {criar.isPending ? 'Criando…' : 'Criar em rascunho'}
                  </Botao>
                </>
              }
            >
              <div className="flex flex-col gap-4">
                <p className="txt-apoio text-pretty text-tinta-secundaria">
                  Ele nasce em rascunho, e é só em rascunho que os itens mudam. Depois de publicado
                  nada muda mais — a inspeção de ontem precisa continuar apontando para a lista de
                  ontem, senão &ldquo;o que foi verificado&rdquo; perde resposta.
                </p>
                <CampoTexto rotulo="Nome" valor={nome} aoMudar={setNome} />
                <CampoSelecao
                  rotulo="Rotina"
                  valor={rotina}
                  aoMudar={setRotina}
                  opcoes={ROTINAS.map((r) => ({ valor: r, rotulo: rotinaEscrita(r) }))}
                />
                <CampoSelecao
                  rotulo="Modelo"
                  valor={modeloId}
                  aoMudar={(v) => {
                    setModeloId(v);
                    if (v !== '') setConfiguracaoId('');
                  }}
                  opcoes={opcoesDeModelo}
                  rotuloVazio={opcoesDeModelo.length === 0 ? 'Nenhum modelo no catálogo' : 'Nenhum (usar a configuração)'}
                  opcional
                />
                <CampoSelecao
                  rotulo="Configuração"
                  valor={configuracaoId}
                  aoMudar={(v) => {
                    setConfiguracaoId(v);
                    if (v !== '') setModeloId('');
                  }}
                  opcoes={opcoesDeConfiguracao}
                  rotuloVazio={opcoesDeConfiguracao.length === 0 ? 'Nenhuma configuração no catálogo' : 'Nenhuma (usar o modelo)'}
                  opcional
                />
                {alvoInvalido ? (
                  <p className="txt-apoio text-tinta-secundaria">
                    Escolha o modelo ou a configuração, um dos dois. O modelo cobre todas as configurações dele. A configuração já
                    pertence a um modelo, e preencher os dois criaria a chance de se contradizerem.
                  </p>
                ) : null}
                {criar.isError ? (
                  <p role="alert" className="txt-apoio text-parada-texto">
                    {explicarFalha(criar.error).comoResolver}
                  </p>
                ) : null}
              </div>
            </JanelaDeAcao>
            ) : undefined
          }
        />

        <Tabela
          rotulo="Checklists"
          colunas={COLUNAS}
          linhas={paginaDosChecklists.visiveis}
          rodape={<RodapeTabela {...paginaDosChecklists.rodape} />}
          chaveDaLinha={(c) => c.id}
          carregando={checklists.isPending}
          erro={
            checklists.isError ? (
              <ErroDaLista oQue="Os checklists" erro={checklists.error} aoTentarDeNovo={() => void checklists.refetch()} />
            ) : null
          }
          alturaDupla
          selecionada={aberto?.id ?? null}
          aoSelecionar={(c) => setAberto(c)}
          acoes={(c) => <MaisAcoes acoes={acoesDoChecklist(c)} />}
          vazio={
            <EstadoVazio
              titulo="Nenhum checklist montado"
              descricao="Crie o checklist de um modelo ou configuração, acrescente os itens e publique-o com a nota da revisão técnica. Só o publicado vira inspeção."
              acao={
                podeGravar ? (
                  <Botao nivel="secundario" onClick={() => setCriando(true)}>
                    Novo checklist
                  </Botao>
                ) : undefined
              }
            />
          }
        />

        {/* -------------------------------------------- os itens do escolhido */}
        {aberto === null ? (
          <p className="txt-apoio text-tinta-secundaria">
            Escolha um checklist na lista acima para ver e montar os itens dele.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-baseline justify-between gap-4">
              <h2 className="txt-secao text-tinta">{`Itens de ${aberto.nome}`}</h2>
              {aberto.situacao === 'rascunho' && podeGravar ? (
                <Botao nivel="secundario" onClick={abrirAcrescentar}>
                  Acrescentar item
                </Botao>
              ) : (
                <span className="txt-apoio text-tinta-secundaria">
                  {aberto.situacao === 'rascunho'
                    ? 'Você não tem a licença para montar a lista.'
                    : 'Publicado: a lista não muda mais.'}
                </span>
              )}
            </div>

            <Tabela
              rotulo={`Itens de ${aberto.nome}`}
              colunas={[
                {
                  chave: 'ordem',
                  rotulo: 'Ordem',
                  alinhamento: 'numero',
                  celula: (i) => <span className="numero text-tinta">{formatarInteiro(i.ordem)}</span>,
                },
                {
                  chave: 'descricao',
                  rotulo: 'O que se verifica',
                  celula: (i) => (
                    <span className="flex min-w-0 flex-col">
                      <span className="txt-corpo text-tinta">{i.descricao}</span>
                      {i.grupo === '' ? null : (
                        <TextoDaCelula texto={i.grupo} classe="txt-apoio text-tinta-apagada" linhas={1} />
                      )}
                    </span>
                  ),
                },
                {
                  chave: 'tipo',
                  rotulo: 'Resposta',
                  prioridade: 'secundaria',
                  celula: (i) => (
                    <span className="txt-corpo text-tinta">
                      {i.tipo === 'conformidade'
                        ? 'Conforme ou não'
                        : i.tipo === 'medida'
                          ? `Medida em ${i.unidade ?? ''}`
                          : 'Texto'}
                    </span>
                  ),
                },
                {
                  chave: 'criticidade',
                  rotulo: 'Criticidade',
                  /* O crítico em vermelho porque é ele que não se encerra sem
                     tratamento — o valor é o próprio alerta (regra 3). */
                  celula: (i) =>
                    i.criticidade === 'critico' ? (
                      <span className="txt-corpo text-parada-texto">{criticidadeEscrita(i.criticidade)}</span>
                    ) : (
                      <span className="txt-corpo text-tinta">{criticidadeEscrita(i.criticidade)}</span>
                    ),
                },
              ]}
              linhas={paginaDosItens.visiveis}
              rodape={<RodapeTabela {...paginaDosItens.rodape} />}
              chaveDaLinha={(i) => i.id}
              carregando={itens.isPending}
              erro={
                itens.isError ? (
                  <ErroDaLista oQue="Os itens do checklist" erro={itens.error} aoTentarDeNovo={() => void itens.refetch()} />
                ) : null
              }
              alturaDupla
              vazio={
                <EstadoVazio
                  titulo="Nenhum item nesta lista"
                  descricao="Acrescente o que a inspeção deve verificar. Item crítico abre ocorrência que não se encerra sem O.S. ou sem o que foi feito escrito; item informativo registra leitura e não abre nada."
                  acao={
                    aberto.situacao === 'rascunho' && podeGravar ? (
                      <Botao nivel="secundario" onClick={abrirAcrescentar}>
                        Acrescentar item
                      </Botao>
                    ) : undefined
                  }
                />
              }
            />
          </>
        )}
      </div>

      <Janela
        titulo="Acrescentar item ao checklist"
        aberta={acrescentando}
        aoFechar={() => setAcrescentando(false)}
        acoes={
          <>
            {/* O motivo do botão desligado, escrito ao lado dele. */}
            {motivoDeNaoAcrescentar ? (
              <span className="txt-apoio mr-auto text-pretty text-tinta-secundaria">{motivoDeNaoAcrescentar}</span>
            ) : null}
            <Botao nivel="terciario" onClick={() => setAcrescentando(false)}>
              Fechar
            </Botao>
            <Botao
              nivel="secundario"
              disabled={motivoDeNaoAcrescentar !== null || acrescentarItem.isPending}
              onClick={() => acrescentarItem.mutate()}
            >
              {acrescentarItem.isPending ? 'Acrescentando…' : 'Acrescentar e continuar'}
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {/* A JANELA NÃO FECHA ao acrescentar: montar trinta itens reabrindo a
              janela trinta vezes é o atrito que faz a lista ficar pela metade.
              O número da ordem avança sozinho. */}
          <p className="txt-apoio text-pretty text-tinta-secundaria">
            A janela fica aberta para o próximo item, e a ordem avança sozinha. A criticidade decide
            o que acontece quando alguém marca &ldquo;não conforme&rdquo;: crítico e atenção abrem
            ocorrência, informativo não abre nada.
          </p>
          {/* EM GRADE, E O QUE O SISTEMA JÁ SABE EM ETIQUETA (31/10/2026): a
              cada item se digita o que se verifica; a posição avança sozinha
              e o grupo fica o do item anterior, e os dois abrem em "Ajustar
              posição e grupo". */}
          <div className="formulario-grade">
            <div className="campo-linha">
              <CampoTexto rotulo="O que se verifica" valor={itemDescricao} aoMudar={setItemDescricao} />
            </div>
            <CampoSelecao
              rotulo="Como se responde"
              valor={itemTipo}
              aoMudar={setItemTipo}
              opcoes={[
                { valor: 'conformidade', rotulo: 'Conforme ou não conforme' },
                { valor: 'medida', rotulo: 'Uma medida: nível, pressão, folga' },
                { valor: 'texto', rotulo: 'Texto livre' },
              ]}
            />
            <CampoSelecao
              rotulo="Criticidade"
              valor={itemCriticidade}
              aoMudar={setItemCriticidade}
              opcoes={[
                { valor: 'informativo', rotulo: 'Informativo', detalhe: 'Registra a leitura, não abre ocorrência' },
                { valor: 'atencao', rotulo: 'Atenção', detalhe: 'Abre ocorrência' },
                { valor: 'critico', rotulo: 'Crítico', detalhe: 'Abre ocorrência que não se encerra sem tratamento' },
              ]}
            />
            {itemTipo === 'medida' ? (
              <CampoTexto
                rotulo="Unidade"
                valor={itemUnidade}
                aoMudar={setItemUnidade}
                ajuda="Obrigatória para medida: um número sem unidade não significa nada."
              />
            ) : null}
            <LinhaDoPreenchido
              rotulo="Onde entra"
              aberta={ajustandoPosicao}
              aoAlternar={() => setAjustandoPosicao((aberto) => !aberto)}
              rotuloDoBotao="posição e grupo"
              idDoConteudo="ajuste-da-posicao-do-item"
            >
              <EtiquetaDoPreenchido rotulo="Posição" valor={itemOrdem || 'Sem posição'} vazia={itemOrdem === ''} marcada />
              <EtiquetaDoPreenchido rotulo="Grupo" valor={itemGrupo.trim() || 'Sem grupo'} vazia={itemGrupo.trim() === ''} />
            </LinhaDoPreenchido>
            {ajustandoPosicao ? (
              <div id="ajuste-da-posicao-do-item" className="campo-linha formulario-grade bloco-rebaixado p-4">
                <CampoTexto rotulo="Ordem" valor={itemOrdem} aoMudar={setItemOrdem} ajuda="A posição do item na lista. Avança sozinha." />
                <CampoTexto
                  rotulo="Grupo"
                  valor={itemGrupo}
                  aoMudar={setItemGrupo}
                  opcional
                  ajuda="Para separar a lista em partes: motor, hidráulico, segurança. Fica o do item anterior."
                />
              </div>
            ) : null}
          </div>
          {acrescentarItem.isError ? (
            <p role="alert" className="txt-apoio text-parada-texto">
              {explicarFalha(acrescentarItem.error).comoResolver}
            </p>
          ) : null}
        </div>
      </Janela>

      <Janela
        titulo="Publicar o checklist"
        aberta={publicando !== null && podePublicar}
        aoFechar={() => setPublicando(null)}
        acoes={
          <>
            <Botao nivel="terciario" onClick={() => setPublicando(null)}>
              Voltar
            </Botao>
            <HistoriaNaMesa codigo="BTN-INS-05" className="flex">
              <Botao
                nivel="secundario"
                disabled={notaDeRevisao.trim().length < 15 || publicar.isPending}
                onClick={() => publicar.mutate()}
              >
                {publicar.isPending ? 'Publicando…' : 'Publicar checklist'}
              </Botao>
            </HistoriaNaMesa>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {/* A MENSAGEM DIZ O QUE VAI ACONTECER, porque o que acontece não volta
              atrás — e porque publicar arquiva o anterior do mesmo alvo. */}
          <p className="txt-corpo text-pretty text-tinta">
            {publicando === null
              ? ''
              : `Publicar "${publicando.nome}", com ${formatarInteiro(publicando.itens)} ${publicando.itens === 1 ? 'item' : 'itens'}? Depois disso a lista não muda mais, e o checklist publicado hoje para ${rotinaEscrita(publicando.rotina).toLowerCase()} no mesmo alvo passa a arquivado.`}
          </p>
          <CampoTexto
            rotulo="Nota da revisão técnica"
            valor={notaDeRevisao}
            aoMudar={setNotaDeRevisao}
            ajuda="Pelo menos 15 caracteres, dizendo o que foi conferido nesta lista. Sem a nota, publicado quer dizer só que alguém clicou."
          />
          {publicar.isError ? (
            <p role="alert" className="txt-apoio text-parada-texto">
              {explicarFalha(publicar.error).comoResolver}
            </p>
          ) : null}
          {publicar.data === undefined ? null : (
            <p role="status" className="txt-apoio text-tinta-secundaria">
              {publicar.data.arquivados === 0
                ? 'Publicado. Não havia outro publicado para este alvo e rotina.'
                : `Publicado, e ${formatarInteiro(publicar.data.arquivados)} ${publicar.data.arquivados === 1 ? 'checklist anterior foi arquivado' : 'checklists anteriores foram arquivados'}.`}
            </p>
          )}
        </div>
      </Janela>
    </div>
  );
}
